import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog, DialogTitle, List, ListItemButton, ListItemAvatar,
    Avatar, ListItemText, CircularProgress, Box, Typography,
    IconButton, Fade, TextField, InputAdornment, Checkbox, Button,
    DialogActions
} from '@mui/material';
import { Search, Close, GroupAdd, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import axiosClient from '../../api/axiosClient';

interface User { id: string; fullName: string; email: string; }

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: (group: any) => void;
}

export const CreateGroupDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [groupTitle, setGroupTitle] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setLoading(true);
            setSearchQuery('');
            setGroupTitle('');
            setSelectedIds([]);
            setError(null);

            // Загружаем список пользователей для добавления
            axiosClient.get('/users').then(res => {
                setUsers(res.data);
                setLoading(false);
            }).catch(() => setLoading(false));
        }
    }, [open]);

    const filteredUsers = useMemo(() => {
        return users.filter(user =>
            user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [users, searchQuery]);

    const handleToggleUser = (userId: string) => {
        setSelectedIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleCreate = async () => {
        if (!groupTitle.trim()) {
            setError('Введите название группы');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axiosClient.post('/groups', {
                title: groupTitle.trim(),
                userIds: selectedIds
            });
            onSuccess(res.data);
            onClose();
        } catch (err: any) {
            setError('Ошибка создания группы');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            TransitionComponent={Fade}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    bgcolor: '#f4f7f6',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupAdd sx={{ color: '#9c27b0' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Новая группа</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small"><Close /></IconButton>
                </Box>

                {/* ПОЛЕ НАЗВАНИЯ ГРУППЫ */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Название группы..."
                    value={groupTitle}
                    onChange={(e) => { setGroupTitle(e.target.value); setError(null); }}
                    error={!!error}
                    helperText={error}
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fff' } }}
                />

                {/* ПОЛЕ ПОИСКА УЧАСТНИКОВ */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Поиск участников..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 3, bgcolor: '#f0f2f5', border: 'none', '& fieldset': { border: 'none' } }
                    }}
                />
            </DialogTitle>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={30} /></Box>
                ) : (
                    <List sx={{ pt: 0 }}>
                        {filteredUsers.map((user) => {
                            const isSelected = selectedIds.includes(user.id);
                            return (
                                <ListItemButton
                                    key={user.id}
                                    onClick={() => handleToggleUser(user.id)}
                                    sx={{ borderRadius: 3, mb: 0.5, '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.08)' } }}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: isSelected ? '#9c27b0' : '#bdbdbd', fontWeight: 600, transition: '0.2s' }}>
                                            {user.fullName[0].toUpperCase()}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={user.fullName}
                                        secondary={user.email}
                                        primaryTypographyProps={{ fontWeight: 600 }}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                    <Checkbox
                                        icon={<RadioButtonUnchecked />}
                                        checkedIcon={<CheckCircle />}
                                        checked={isSelected}
                                        sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}
            </Box>

            <DialogActions sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" sx={{ flexGrow: 1, ml: 1, opacity: 0.7 }}>
                    Выбрано: {selectedIds.length}
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={submitting || !groupTitle.trim()}
                    sx={{
                        borderRadius: 3,
                        bgcolor: '#9c27b0',
                        '&:hover': { bgcolor: '#7b1fa2' },
                        boxShadow: 'none',
                        px: 3,
                        fontWeight: 700
                    }}
                >
                    {submitting ? <CircularProgress size={20} color="inherit" /> : 'Создать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
